'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FILTER_TAG_KEYS, MULTI_SELECT_FILTER_KEYS } from '../../../constants/search/Filters';
import { COUNTRY, STATE, SORTS } from '../../../constants/search/FilterKey';
import StorefrontUsageTrackerContext from '../../../context/StorefrontUsageTrackerContext';
import { updateStorefrontFilters, storefrontFiltersInitialState, DEFAULT_SORT } from '../../../slices/storefrontFiltersSlice';
import UsageViewTracker from '../../tracking/UsageViewTracker';
import * as FilterKey from '../../../constants/search/FilterKey';
import SortPanel from './SortPanel';
import { US } from 'ecosystem-marketplace-core/constants/solutions/Country';
import MobilePanelNavItem from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelNavItem';
import MobilePanelNav from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelNav';
import MobilePanel from 'ecosystem-marketplace-core/js/components/mobile/MobilePanel';
import MobilePanelHeader from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelHeader';
import MobilePanelBody from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelBody';
import UIButton from 'UIComponents/button/UIButton';
import MobilePanelFooter from 'ecosystem-marketplace-core/js/components/mobile/MobilePanelFooter';
import * as RequestState from 'ecosystem-marketplace-core/constants/RequestState';
import FilterDrilldownPanel from './FilterDrilldownPanel';
import { getServicesRequestState } from '../../../slices/serviceCatalogSlice';
const textPath = 'ecosystem-marketplace-solutions-ui.storefront.filters';
const CLOSE_FILTER_PANEL = 'CLOSE_FILTER_PANEL';
const RESET_SORT_AND_FILTER = 'RESET_SORT_AND_FILTER';
const SET_ACTIVE_FILTERS = 'SET_ACTIVE_FILTERS';
const SET_ACTIVE_PANEL_IDS = 'SET_ACTIVE_PANEL_IDS';
const SET_ACTIVE_SORT = 'SET_ACTIVE_SORT';
const STAGE_FILTER_FOR_REMOVAL = 'STAGE_FILTER_FOR_REMOVAL';
const CLEAR_FILTERS_FOR_REMOVAL = 'CLEAR_FILTERS_FOR_REMOVAL';
const initialState = {
  activePanelIds: [],
  activeFilters: [],
  filtersToRemove: [],
  activeSort: null
};

const reducer = (state, action) => {
  switch (action.type) {
    case CLOSE_FILTER_PANEL:
      {
        return Object.assign({}, state, {
          activeFilters: action.payload.filters,
          activeSort: action.payload.initialSort,
          activePanelIds: []
        });
      }

    case RESET_SORT_AND_FILTER:
      {
        return Object.assign({}, state, {
          activeFilters: action.payload.filters,
          activeSort: action.payload.initialSort
        });
      }

    case SET_ACTIVE_FILTERS:
      {
        return Object.assign({}, state, {
          activeFilters: action.payload
        });
      }

    case SET_ACTIVE_PANEL_IDS:
      {
        return Object.assign({}, state, {
          activePanelIds: action.payload
        });
      }

    case SET_ACTIVE_SORT:
      {
        return Object.assign({}, state, {
          activeSort: action.payload
        });
      }

    case STAGE_FILTER_FOR_REMOVAL:
      {
        const {
          filterKey
        } = action.payload;
        return Object.assign({}, state, {
          activeFilters: Object.assign({}, state.activeFilters, {
            [filterKey]: storefrontFiltersInitialState[filterKey]
          }),
          filtersToRemove: [...state.filtersToRemove, filterKey]
        });
      }

    case CLEAR_FILTERS_FOR_REMOVAL:
      {
        return Object.assign({}, state, {
          filtersToRemove: []
        });
      }

    default:
      return state;
  }
};

const FiltersPanel = ({
  filters,
  onClearFilters,
  onDeleteFilter,
  onPanelClose,
  onSetFilter,
  open,
  onSelectedFilterChange
}) => {
  const initialSort = filters[SORTS];
  const reduxDispatch = useDispatch();
  const servicesRequestState = useSelector(getServicesRequestState);
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const applyButtonRef = useRef(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    activeFilters,
    activePanelIds,
    activeSort,
    filtersToRemove
  } = state;
  const FILTER_KEYS_TO_RENDER = useMemo(() => {
    return filters[COUNTRY] === US ? FILTER_TAG_KEYS : FILTER_TAG_KEYS.filter(key => key !== STATE);
  }, [filters]);
  useEffect(() => {
    dispatch({
      type: RESET_SORT_AND_FILTER,
      payload: {
        filters,
        initialSort
      }
    });
  }, [filters, initialSort]);
  const clickFilter = useCallback(value => {
    onSelectedFilterChange(value);

    if (activePanelIds && activePanelIds.indexOf(value) === -1) {
      dispatch({
        type: SET_ACTIVE_PANEL_IDS,
        payload: [...activePanelIds, value]
      });
    }
  }, [activePanelIds, onSelectedFilterChange]);
  const closeFilterPanel = useCallback(() => {
    dispatch({
      type: CLOSE_FILTER_PANEL,
      payload: {
        filters,
        initialSort
      }
    });
    onPanelClose(false);
  }, [filters, initialSort, onPanelClose]);
  const updateSort = useCallback(sort => {
    reduxDispatch(updateStorefrontFilters({
      updates: {
        [SORTS]: sort
      }
    }));
    trackInteraction('change sort', {
      sortField: sort
    });
  }, [reduxDispatch, trackInteraction]);
  const applyFilters = useCallback(() => {
    // only set filters that weren't previously set.
    const newFilters = Object.keys(activeFilters).filter(filterKey => Object.keys(filters).indexOf(filterKey) === -1 || filters[filterKey] !== activeFilters[filterKey]).reduce((obj, filterKey) => {
      obj[filterKey] = activeFilters[filterKey];
      return obj;
    }, {});
    onSetFilter(newFilters);
    onDeleteFilter(filtersToRemove);
    dispatch({
      type: CLEAR_FILTERS_FOR_REMOVAL
    }); // update sort if it's changed

    if (initialSort !== activeSort) {
      updateSort(activeSort);
    }

    closeFilterPanel();
  }, [activeFilters, activeSort, closeFilterPanel, filtersToRemove, onDeleteFilter, onSetFilter, filters, initialSort, updateSort]);
  const closeDrilldown = useCallback(value => {
    const newActiveItemIds = [...activePanelIds];
    newActiveItemIds.splice(newActiveItemIds.indexOf(value), 1);
    applyButtonRef.current.focus();
    dispatch({
      type: SET_ACTIVE_PANEL_IDS,
      payload: newActiveItemIds
    });
  }, [activePanelIds]);
  const addFilter = useCallback((filterKey, filterValue) => {
    dispatch({
      type: SET_ACTIVE_FILTERS,
      payload: Object.assign({}, activeFilters, {
        [filterKey]: filterValue
      })
    });
  }, [activeFilters]);
  const removeFilter = useCallback(filterKey => {
    dispatch({
      type: STAGE_FILTER_FOR_REMOVAL,
      payload: {
        filterKey
      }
    });
  }, []);
  const filterPanels = useMemo(() => {
    return FILTER_KEYS_TO_RENDER.map(filterKey => {
      return filterKey === FilterKey.SERVICE && servicesRequestState === RequestState.FAILED ? null : /*#__PURE__*/_jsx(MobilePanelNavItem, {
        onClickItem: clickFilter,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.${filterKey}.panelLabel`,
          options: {
            count: activeFilters[filterKey] ? typeof activeFilters[filterKey] === 'string' ? 1 : activeFilters[filterKey].length : 0
          }
        }),
        itemId: filterKey,
        children: /*#__PURE__*/_jsxs(MobilePanel, {
          open: activePanelIds && activePanelIds.indexOf(filterKey) !== -1,
          align: "right",
          children: [/*#__PURE__*/_jsx(FilterDrilldownPanel, {
            addFilter: addFilter,
            applyFilters: applyFilters,
            onPanelClose: () => closeDrilldown(filterKey),
            onRemoveFilter: removeFilter,
            filterType: filterKey,
            multi: MULTI_SELECT_FILTER_KEYS.includes(filterKey),
            selectedValue: activeFilters[filterKey]
          }), /*#__PURE__*/_jsx(UsageViewTracker, {
            name: "storefront-mobile-filter-panel-view",
            properties: {
              screen: 'storefront',
              subscreen: 'mobile filter panel',
              filterKey
            }
          })]
        })
      }, filterKey);
    });
  }, [FILTER_KEYS_TO_RENDER, servicesRequestState, clickFilter, activeFilters, activePanelIds, addFilter, applyFilters, removeFilter, closeDrilldown]);
  return /*#__PURE__*/_jsxs(MobilePanel, {
    align: 'bottom',
    open: open,
    children: [/*#__PURE__*/_jsx(MobilePanelHeader, {
      itemLeft: /*#__PURE__*/_jsx(UIButton, {
        autoFocus: true,
        use: "transparent",
        onClick: closeFilterPanel,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.cancel`
        })
      }),
      title: I18n.text(`${textPath}.title`),
      itemRight: /*#__PURE__*/_jsx(UIButton, {
        use: "transparent",
        onClick: applyFilters,
        tabIndex: -1,
        children: /*#__PURE__*/_jsx("div", {
          ref: applyButtonRef,
          tabIndex: 0,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `${textPath}.apply`
          })
        })
      })
    }), /*#__PURE__*/_jsxs(MobilePanelBody, {
      children: [/*#__PURE__*/_jsx(MobilePanelNav, {
        title: I18n.text(`${textPath}.sortTitle`),
        children: /*#__PURE__*/_jsx(SortPanel, {
          activeSort: activeSort,
          applyFilters: applyFilters,
          activePanelIds: activePanelIds,
          clickFilter: clickFilter,
          filters: filters,
          onPanelClose: closeDrilldown,
          onSortChange: sort => {
            dispatch({
              type: SET_ACTIVE_SORT,
              payload: sort
            });
          }
        })
      }), /*#__PURE__*/_jsx(MobilePanelNav, {
        title: I18n.text(`${textPath}.filterTitle`),
        children: filterPanels
      })]
    }), /*#__PURE__*/_jsx(MobilePanelFooter, {
      children: /*#__PURE__*/_jsx(UIButton, {
        onClick: () => {
          onClearFilters();
          updateSort(DEFAULT_SORT);
          closeFilterPanel();
        },
        use: "link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.clearAll`
        })
      })
    }), /*#__PURE__*/_jsx(UsageViewTracker, {
      name: "storefront-mobile-filter-panel-view",
      properties: {
        screen: 'storefront',
        subscreen: 'mobile filter panel'
      }
    })]
  });
};

FiltersPanel.propTypes = {
  filters: PropTypes.object.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onDeleteFilter: PropTypes.func.isRequired,
  onPanelClose: PropTypes.func.isRequired,
  onSetFilter: PropTypes.func.isRequired,
  open: PropTypes.bool,
  onSelectedFilterChange: PropTypes.func.isRequired
};
export default FiltersPanel;